import { memo, useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { Empty, Spin, Typography } from 'antd'
import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy } from 'pdfjs-dist'
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import './PdfJsScrollViewer.css'

GlobalWorkerOptions.workerSrc = pdfWorker

const RENDER_SCALE = 1.35
const IO_ROOT_MARGIN = '480px 0px'

type PageSize = { width: number; height: number }

const PdfLazyPage = memo(function PdfLazyPage({
  pdf,
  pageNumber,
  onRendered,
}: {
  pdf: PDFDocumentProxy
  pageNumber: number
  onRendered?: (size: PageSize) => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [rendering, setRendering] = useState(true)
  const [size, setSize] = useState<PageSize | null>(null)

  useEffect(() => {
    let cancelled = false
    setRendering(true)

    void (async () => {
      try {
        const page = await pdf.getPage(pageNumber)
        if (cancelled) return
        const viewport = page.getViewport({ scale: RENDER_SCALE })
        const nextSize = { width: viewport.width, height: viewport.height }
        setSize(nextSize)
        onRendered?.(nextSize)

        const canvas = canvasRef.current
        if (!canvas || cancelled) return
        canvas.width = viewport.width
        canvas.height = viewport.height
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        await page.render({ canvasContext: ctx, viewport }).promise
        if (!cancelled) setRendering(false)
      } catch {
        if (!cancelled) setRendering(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [pdf, pageNumber, onRendered])

  return (
    <div
      className="pdfjs-page"
      style={
        size
          ? { width: size.width, maxWidth: '100%' }
          : { minHeight: 420, width: '100%', maxWidth: '100%' }
      }
    >
      {rendering ? (
        <div className="pdfjs-page-loading">
          <Spin size="small" />
        </div>
      ) : null}
      <canvas ref={canvasRef} />
    </div>
  )
})

type Props = {
  url: string | null
  scrollRef?: RefObject<HTMLDivElement | null>
  onScroll?: () => void
  className?: string
}

export function PdfJsScrollViewer({ url, scrollRef, onScroll, className }: Props) {
  const innerScrollRef = useRef<HTMLDivElement>(null)
  const slotRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const [docLoading, setDocLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null)
  const [numPages, setNumPages] = useState(0)
  const [pageSize, setPageSize] = useState<PageSize | null>(null)
  const [visiblePages, setVisiblePages] = useState<Set<number>>(() => new Set([1]))

  const setScrollRef = useCallback(
    (el: HTMLDivElement | null) => {
      innerScrollRef.current = el
      if (scrollRef && 'current' in scrollRef) {
        ;(scrollRef as { current: HTMLDivElement | null }).current = el
      }
    },
    [scrollRef],
  )

  const markVisible = useCallback((pageNumber: number) => {
    setVisiblePages((prev) => {
      if (prev.has(pageNumber)) return prev
      const next = new Set(prev)
      next.add(pageNumber)
      return next
    })
  }, [])

  useEffect(() => {
    if (!url) {
      setPdf(null)
      setNumPages(0)
      setPageSize(null)
      setError(null)
      setVisiblePages(new Set())
      return
    }

    let cancelled = false
    setDocLoading(true)
    setError(null)
    setPdf(null)
    setNumPages(0)
    setVisiblePages(new Set([1]))

    void (async () => {
      try {
        const doc = await getDocument({ url, withCredentials: true }).promise
        if (cancelled) return
        setPdf(doc)
        setNumPages(doc.numPages)
        const first = await doc.getPage(1)
        if (cancelled) return
        const vp = first.getViewport({ scale: RENDER_SCALE })
        setPageSize({ width: vp.width, height: vp.height })
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'PDF 加载失败')
        }
      } finally {
        if (!cancelled) setDocLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [url])

  useEffect(() => {
    if (!pdf || !numPages) return
    const root = innerScrollRef.current
    if (!root) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const pageNumber = Number(entry.target.getAttribute('data-pdf-page'))
          if (Number.isFinite(pageNumber) && pageNumber > 0) markVisible(pageNumber)
        }
      },
      { root, rootMargin: IO_ROOT_MARGIN, threshold: 0.01 },
    )

    slotRefs.current.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [pdf, numPages, markVisible])

  if (!url) {
    return <Empty description="PDF 未加载" className="pdfjs-empty" />
  }

  if (docLoading && !pdf) {
    return (
      <div className="pdfjs-loading">
        <Spin tip="打开 PDF…" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="pdfjs-error">
        <Empty description={error} />
      </div>
    )
  }

  if (!pdf || numPages < 1) {
    return (
      <div className="pdfjs-error">
        <Empty description="无法打开 PDF" />
      </div>
    )
  }

  const slotMinHeight = (pageSize?.height ?? 420) + 24

  return (
    <div className={className ? `pdfjs-viewer ${className}` : 'pdfjs-viewer'}>
      <div ref={setScrollRef} className="pdfjs-scroll" onScroll={onScroll}>
        {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => (
          <div
            key={pageNumber}
            ref={(el) => {
              if (el) slotRefs.current.set(pageNumber, el)
              else slotRefs.current.delete(pageNumber)
            }}
            data-pdf-page={pageNumber}
            className="pdfjs-page-slot"
            style={{ minHeight: slotMinHeight }}
          >
            {visiblePages.has(pageNumber) ? (
              <PdfLazyPage pdf={pdf} pageNumber={pageNumber} />
            ) : (
              <div className="pdfjs-page-placeholder">
                <Typography.Text type="secondary">第 {pageNumber} 页</Typography.Text>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
